/*
 * Vital Pantry - gifting
 *
 * Two jobs, both of which the theme has no template hook for, so both are
 * injected the way vp-delivery.js, vp-freeship.js and vp-addon.js do it: the
 * theme re-renders the cart's own contents wholesale, so anything that has to
 * survive an add or a quantity change must sit just outside them.
 *
 *   1. Gift options in the cart drawer and on the cart page. A tick box, a
 *      message and a "from" name, written to CART ATTRIBUTES so they land on
 *      the order in admin next to the delivery date, which is already kept the
 *      same way (see ATTR_* in vp-delivery.js).
 *
 *   2. A recipient form on the gift card product page, so the card is emailed
 *      straight to the person it is for, on a chosen date, with a message.
 *      This is Shopify's own feature and it is driven entirely by line item
 *      properties with exact names - see PROP_* below. Nothing here talks to
 *      an API; the names are the contract.
 *      https://shopify.dev/docs/storefronts/themes/product-merchandising/gift-cards
 *
 * Attributes are saved with XMLHttpRequest rather than fetch ON PURPOSE.
 * vp-delivery.js wraps window.fetch to watch for /cart/(add|change|update|clear)
 * and re-opens its date modal whenever the cart moves and no date is set. That
 * is right for items changing; it is wrong for someone typing a gift message,
 * who would get the postcode modal thrown at them on every keystroke pause.
 * XHR is not wrapped, so an attribute-only write stays quiet.
 */
(function () {
  'use strict';

  var cfg = window.VPGift || {};

  // Cart attributes. Readable, because they are read by a human packing a box.
  var ATTR_GIFT = 'Gift';
  var ATTR_MESSAGE = 'Gift message';
  var ATTR_FROM = 'Gift from';

  // Shopify's gift card recipient properties. These names are fixed.
  var PROP_SEND = 'properties[__shopify_send_gift_card_to_recipient]';
  var PROP_OFFSET = 'properties[__shopify_offset]';
  var PROP_EMAIL = 'properties[Recipient email]';
  var PROP_NAME = 'properties[Recipient name]';
  var PROP_MESSAGE = 'properties[Message]';
  var PROP_SEND_ON = 'properties[Send on]';

  var MESSAGE_MAX = 200;   // Shopify rejects a longer gift card message
  var NAME_MAX = 255;      // and a longer recipient name
  var SCHEDULE_MAX_DAYS = 90;

  var state = {
    on: !!(cfg.initial && cfg.initial.gift),
    message: (cfg.initial && cfg.initial.message) || '',
    from: (cfg.initial && cfg.initial.from) || ''
  };
  var hasItems = !!(cfg.cart && cfg.cart.count > 0);

  // A cart holding nothing but gift cards has no parcel to put a note in, and
  // the helper text below the tick box talks about delivery addresses and
  // postcodes - which reads like a mistake next to a single emailed card. The
  // card carries its own message through the recipient form instead.
  // Seeded from Liquid (cart.requires_shipping) so the box does not flash in
  // and then remove itself; /cart.js replaces it as soon as it answers.
  var needsShipping = !(cfg.cart && cfg.cart.requiresShipping === false);

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ------------------------------------------------------------------ *
   * 1. Gift options in the cart
   * ------------------------------------------------------------------ */

  function buildCartBox(where) {
    var box = document.createElement('div');
    box.className = 'vpg vpg--' + where;
    box.setAttribute('data-vpg', where);
    box.hidden = true;

    box.innerHTML =
      '<label class="vpg__toggle">' +
        '<input type="checkbox" data-vpg-on' + (state.on ? ' checked' : '') + '>' +
        '<span class="vpg__toggle-text">' + escapeHtml(cfg.heading || 'This order is a gift') + '</span>' +
      '</label>' +
      '<div class="vpg__fields" data-vpg-fields' + (state.on ? '' : ' hidden') + '>' +
        '<p class="vpg__hint">' + escapeHtml(cfg.hint || '') + '</p>' +
        '<label class="vpg__label" for="vpg-msg-' + where + '">Message to include</label>' +
        '<textarea class="vpg__input vpg__textarea" id="vpg-msg-' + where + '" data-vpg-message rows="3"' +
          ' maxlength="' + MESSAGE_MAX + '" placeholder="Thinking of you - enjoy."></textarea>' +
        '<p class="vpg__count"><span data-vpg-count>0</span>/' + MESSAGE_MAX + '</p>' +
        '<label class="vpg__label" for="vpg-from-' + where + '">From</label>' +
        '<input class="vpg__input" type="text" id="vpg-from-' + where + '" data-vpg-from maxlength="60"' +
          ' placeholder="Your name" autocomplete="off">' +
        '<p class="vpg__saved" data-vpg-saved hidden>Saved with your order.</p>' +
      '</div>';

    return box;
  }

  function injectCartBoxes() {
    // Drawer: the bottom of the scrolling item list, the same anchor
    // vp-addon.js uses, so the gift options sit above the delivery date, the
    // free delivery bar and Check Out rather than below them. Both anchors are
    // outside the block the theme replaces, so this survives every re-render.
    // The guard is document-wide rather than scoped to the anchor, because the
    // two anchors below are not guaranteed to be inside the same element and a
    // scoped check would happily inject a second copy.
    if (!document.querySelector('[data-vpg="drawer"]')) {
      var items = document.getElementById('min-cart-items');
      var links = document.querySelector('#cart-notification .cart_notification_links_inner');
      if (items) {
        items.appendChild(buildCartBox('drawer'));
      } else if (links) {
        links.insertBefore(buildCartBox('drawer'), links.firstChild);
      }
    }

    // Cart page: directly above the subtotal block, which is re-rendered while
    // its parent is not.
    var blocks = document.querySelector('#main-cart-footer .cart__blocks');
    if (blocks && !blocks.parentNode.querySelector('[data-vpg]')) {
      blocks.parentNode.insertBefore(buildCartBox('page'), blocks);
    }
  }

  function renderCartBoxes() {
    document.querySelectorAll('[data-vpg]').forEach(function (box) {
      // Gift options above "Your cart is empty", or above a cart of nothing but
      // emailed gift cards, are just noise.
      box.hidden = !hasItems || !needsShipping;

      var toggle = box.querySelector('[data-vpg-on]');
      var fields = box.querySelector('[data-vpg-fields]');
      var msg = box.querySelector('[data-vpg-message]');
      var from = box.querySelector('[data-vpg-from]');
      var count = box.querySelector('[data-vpg-count]');

      if (toggle) toggle.checked = state.on;
      if (fields) fields.hidden = !state.on;
      // Never clobber what someone is in the middle of typing.
      if (msg && document.activeElement !== msg) msg.value = state.message;
      if (from && document.activeElement !== from) from.value = state.from;
      if (count) count.textContent = String((msg ? msg.value : state.message).length);
    });
  }

  function showSaved() {
    document.querySelectorAll('[data-vpg-saved]').forEach(function (el) {
      el.hidden = false;
      clearTimeout(el._vpgTimer);
      el._vpgTimer = setTimeout(function () { el.hidden = true; }, 2500);
    });
  }

  /**
   * Attribute-only write. See the file header for why this is XHR and not
   * fetch. Fire and forget: nothing on the page depends on the response, and a
   * failed write must not throw an error in front of someone mid-sentence.
   */
  function saveAttributes() {
    var attrs = {};
    attrs[ATTR_GIFT] = state.on ? 'Yes' : '';
    attrs[ATTR_MESSAGE] = state.on ? state.message : '';
    attrs[ATTR_FROM] = state.on ? state.from : '';

    try {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', rootUrl() + 'cart/update.js', true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = function () { if (xhr.status >= 200 && xhr.status < 300) showSaved(); };
      xhr.send(JSON.stringify({ attributes: attrs }));
    } catch (e) {
      /* a gift message is not worth breaking a checkout over */
    }
  }

  var saveTimer = null;
  function saveSoon() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(saveAttributes, 600);
  }

  function refreshCart() {
    return fetch(rootUrl() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        hasItems = (c.item_count || 0) > 0;
        needsShipping = (c.items || []).some(function (it) { return it.requires_shipping !== false; });
        var a = c.attributes || {};
        // The cart is the source of truth across tabs and page loads - except
        // while someone is mid-sentence in one of these boxes, when a slow
        // reply carrying the pre-save value would wipe out what they just typed.
        var el = document.activeElement;
        var typing = el && el.hasAttribute &&
          (el.hasAttribute('data-vpg-message') || el.hasAttribute('data-vpg-from'));
        if (typing) return renderCartBoxes();
        state.on = a[ATTR_GIFT] === 'Yes';
        state.message = a[ATTR_MESSAGE] || '';
        state.from = a[ATTR_FROM] || '';
        renderCartBoxes();
      })
      .catch(function () { /* keep whatever we last knew */ });
  }

  function bindCart() {
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t.hasAttribute || !t.hasAttribute('data-vpg-on')) return;
      state.on = t.checked;
      renderCartBoxes();
      saveAttributes();
    });

    document.addEventListener('input', function (e) {
      var t = e.target;
      if (!t.hasAttribute) return;
      if (t.hasAttribute('data-vpg-message')) {
        state.message = t.value.slice(0, MESSAGE_MAX);
        var box = t.closest('[data-vpg]');
        var count = box && box.querySelector('[data-vpg-count]');
        if (count) count.textContent = String(state.message.length);
        saveSoon();
      } else if (t.hasAttribute('data-vpg-from')) {
        state.from = t.value;
        saveSoon();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 2. Gift card recipient form
   * ------------------------------------------------------------------ */

  function isoDay(offsetDays) {
    var d = new Date();
    d.setDate(d.getDate() + (offsetDays || 0));
    var m = d.getMonth() + 1;
    var day = d.getDate();
    return d.getFullYear() + '-' + (m < 10 ? '0' + m : m) + '-' + (day < 10 ? '0' + day : day);
  }

  /**
   * The main product form only. The sticky buy bar and the product cards carry
   * their own add-to-cart forms, and a second copy of this form in any of them
   * would submit a second, empty set of properties.
   */
  function mainGiftCardForm() {
    var pf = document.querySelector('product-form.product_buy_button_form');
    return pf ? pf.querySelector('form[action*="/cart/add"]') : null;
  }

  function buildRecipientForm() {
    var box = document.createElement('div');
    box.className = 'vpg-rcpt';
    box.setAttribute('data-vpg-rcpt', '');

    box.innerHTML =
      '<label class="vpg__toggle">' +
        '<input type="checkbox" data-vpg-rcpt-on>' +
        '<span class="vpg__toggle-text">Email it straight to them</span>' +
      '</label>' +
      '<div class="vpg-rcpt__fields" data-vpg-rcpt-fields hidden>' +
        '<p class="vpg__hint">We will send the card to their inbox with your message. ' +
          'Leave this unticked and the code comes to you instead, to pass on however you like.</p>' +

        '<label class="vpg__label" for="vpg-rcpt-email">Their email address</label>' +
        '<input class="vpg__input" type="email" id="vpg-rcpt-email" name="' + PROP_EMAIL + '"' +
          ' autocomplete="off" placeholder="them@example.com">' +

        '<label class="vpg__label" for="vpg-rcpt-name">Their name <span class="vpg__opt">(optional)</span></label>' +
        '<input class="vpg__input" type="text" id="vpg-rcpt-name" name="' + PROP_NAME + '"' +
          ' maxlength="' + NAME_MAX + '" autocomplete="off">' +

        '<label class="vpg__label" for="vpg-rcpt-msg">Your message <span class="vpg__opt">(optional)</span></label>' +
        '<textarea class="vpg__input vpg__textarea" id="vpg-rcpt-msg" name="' + PROP_MESSAGE + '"' +
          ' rows="3" maxlength="' + MESSAGE_MAX + '"></textarea>' +
        '<p class="vpg__count"><span data-vpg-rcpt-count>0</span>/' + MESSAGE_MAX + '</p>' +

        '<label class="vpg__label" for="vpg-rcpt-date">Send on <span class="vpg__opt">(optional - leave blank to send now)</span></label>' +
        '<input class="vpg__input" type="date" id="vpg-rcpt-date" name="' + PROP_SEND_ON + '"' +
          ' min="' + isoDay(0) + '" max="' + isoDay(SCHEDULE_MAX_DAYS) + '">' +

        '<input type="hidden" name="' + PROP_SEND + '" value="true">' +
        '<input type="hidden" name="' + PROP_OFFSET + '" value="' + new Date().getTimezoneOffset() + '">' +
      '</div>';

    return box;
  }

  /**
   * Unchecked means "do not send any of this". Disabled controls are left out
   * of FormData, which is what the theme builds its add-to-cart body from, so
   * disabling is enough - no need to tear the fields out of the DOM.
   *
   * The accelerated checkout buttons are hidden while the form is in use
   * because Shopify does not run recipient validation on them, so a typo in
   * the email address would go through unnoticed.
   */
  function syncRecipient(box) {
    var on = box.querySelector('[data-vpg-rcpt-on]').checked;
    var fields = box.querySelector('[data-vpg-rcpt-fields]');
    var email = box.querySelector('[name="' + PROP_EMAIL + '"]');

    fields.hidden = !on;
    fields.querySelectorAll('input, textarea').forEach(function (el) { el.disabled = !on; });
    if (email) email.required = on;

    var form = box.closest('form');
    if (form) {
      form.querySelectorAll('.shopify-payment-button, .product-form__submit-dynamic')
        .forEach(function (el) { el.hidden = on; });
    }
  }

  function injectRecipientForm() {
    if (!cfg.giftCard) return;

    var form = mainGiftCardForm();
    if (!form || form.querySelector('[data-vpg-rcpt]')) return;

    var buttons = form.querySelector('.product-form__buttons');
    var box = buildRecipientForm();
    if (buttons) {
      form.insertBefore(box, buttons);
    } else {
      form.appendChild(box);
    }
    syncRecipient(box);

    // One buy path only. The sticky bar posts its own form, which has no
    // recipient fields in it, so a card bought from there would quietly lose
    // the message and the email address.
    document.querySelectorAll('.sticky__form_button').forEach(function (el) {
      var sticky = el.closest('.product-sticky-cart, .sticky-cart, product-form') || el;
      sticky.hidden = true;
    });

    form.addEventListener('change', function (e) {
      if (e.target.hasAttribute && e.target.hasAttribute('data-vpg-rcpt-on')) syncRecipient(box);
    });

    form.addEventListener('input', function (e) {
      if (!e.target.name || e.target.name !== PROP_MESSAGE) return;
      var count = box.querySelector('[data-vpg-rcpt-count]');
      if (count) count.textContent = String(e.target.value.length);
    });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    injectRecipientForm();

    injectCartBoxes();
    renderCartBoxes();
    bindCart();

    // The same signal vp-addon.js and vp-freeship.js watch: the theme replaces
    // one of these on every add, remove and quantity change.
    var timer = null;
    var soon = function () { clearTimeout(timer); timer = setTimeout(function () {
      injectCartBoxes();
      refreshCart();
    }, 150); };

    ['cart-notification-product', 'main-cart-items'].forEach(function (elId) {
      var el = document.getElementById(elId);
      if (el && window.MutationObserver) {
        new MutationObserver(soon).observe(el, { childList: true, subtree: true });
      }
    });

    // Opening the drawer from the header icon only slides it in; nothing is
    // re-rendered, so nothing above fires.
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('open-minicart, [data-vpd-open]')) soon();
    }, true);

    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, soon);
    }

    refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
